/**
 * DV-INF-002: Demo Viewer WebSocket Client
 *
 * Minimal WebSocket layer for cache invalidation hints only.
 * Does NOT stream real-time data - that's handled by React Query polling.
 * Only provides hints to invalidate specific cache keys for better UX.
 */

import { QueryClient } from '@tanstack/react-query';
import { demoCacheInvalidation, demoQueryKeys } from './demo-query-client';

// WebSocket event types for cache invalidation hints
export interface DemoWebSocketEvent {
  type: 'execution_updated' | 'stage_completed' | 'billing_commit' | 'temporal_event' | 'heartbeat';
  executionId: string;
  stageName?: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

export interface DemoWebSocketHint {
  event: DemoWebSocketEvent;
  cacheKeys: string[];
  action: 'invalidate' | 'refetch';
}

interface DemoWebSocketClientOptions {
  queryClient: QueryClient;
  wsUrl?: string;
  enableLogging?: boolean;
  onEvent?: (event: DemoWebSocketEvent) => void;
  onError?: (error: Error) => void;
  onConnectionChange?: (connected: boolean) => void;
}

export class DemoWebSocketClient {
  private ws: WebSocket | null = null;
  private queryClient: QueryClient;
  private wsUrl: string;
  private enableLogging: boolean;
  private reconnectTimeoutId: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 2; // Reduced since demo WS endpoint doesn't exist yet
  private reconnectInterval = 1000; // Start with 1 second
  private isConnected = false;

  // Event callbacks
  private onEvent?: (event: DemoWebSocketEvent) => void;
  private onError?: (error: Error) => void;
  private onConnectionChange?: (connected: boolean) => void;

  constructor(options: DemoWebSocketClientOptions) {
    this.queryClient = options.queryClient;
    this.wsUrl = options.wsUrl || this.getDefaultWebSocketUrl();
    this.enableLogging = options.enableLogging ?? false;
    this.onEvent = options.onEvent;
    this.onError = options.onError;
    this.onConnectionChange = options.onConnectionChange;
  }

  private getDefaultWebSocketUrl(): string {
    // Use same host as API but switch to WebSocket protocol
    const apiUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.hi-si.ai';
    const wsUrl = apiUrl.replace('https://', 'wss://').replace('http://', 'ws://');

    // For now, try the main WebSocket endpoint instead of /ws/demo
    // TODO: Implement /ws/demo endpoint on backend for cache invalidation hints
    return `${wsUrl}/ws`;
  }

  private log(message: string, data?: any): void {
    if (this.enableLogging) {
      console.log(`[DemoWS] ${message}`, data || '');
    }
  }

  private setConnectionStatus(connected: boolean): void {
    if (this.isConnected !== connected) {
      this.isConnected = connected;
      this.onConnectionChange?.(connected);
      this.log(`Connection status changed: ${connected ? 'connected' : 'disconnected'}`);
    }
  }

  /**
   * Connect to the WebSocket server
   */
  public connect(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.log('Already connected');
      return;
    }

    try {
      this.log(`Connecting to ${this.wsUrl}`);
      this.ws = new WebSocket(this.wsUrl);

      this.ws.onopen = () => {
        this.log('Connected successfully');
        this.setConnectionStatus(true);
        this.reconnectAttempts = 0;
        this.reconnectInterval = 1000; // Reset reconnect interval

        // Send initial subscription for demo events
        this.send({
          type: 'subscribe',
          channels: ['demo_cache_hints']
        });
      };

      this.ws.onmessage = (event) => {
        try {
          const hint: DemoWebSocketHint = JSON.parse(event.data);
          this.handleCacheHint(hint);
        } catch (error) {
          this.log('Failed to parse WebSocket message', { error, data: event.data });
        }
      };

      this.ws.onerror = (error) => {
        this.log('WebSocket error (expected if demo WS not implemented)', error);

        // Only call onError for the first few attempts to avoid spam
        if (this.reconnectAttempts < 3) {
          this.onError?.(new Error('WebSocket connection error - demo cache invalidation disabled'));
        }
      };

      this.ws.onclose = (event) => {
        this.log('Connection closed', { code: event.code, reason: event.reason });
        this.setConnectionStatus(false);
        this.ws = null;

        // Auto-reconnect unless explicitly closed
        if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect();
        }
      };

    } catch (error) {
      this.log('Failed to create WebSocket connection', error);
      this.onError?.(error as Error);
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  public disconnect(): void {
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    if (this.ws) {
      this.log('Disconnecting...');
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }

    this.setConnectionStatus(false);
    this.reconnectAttempts = 0;
  }

  /**
   * Check if WebSocket is currently connected
   */
  public isConnectionOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Send message to WebSocket server
   */
  private send(message: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
      this.log('Sent message', message);
    } else {
      this.log('Cannot send message - WebSocket not connected', message);
    }
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.log('Max reconnection attempts reached');
      this.onError?.(new Error('WebSocket max reconnection attempts exceeded'));
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1), 30000);

    this.log(`Scheduling reconnect attempt ${this.reconnectAttempts} in ${delay}ms`);

    this.reconnectTimeoutId = setTimeout(() => {
      this.log(`Reconnection attempt ${this.reconnectAttempts}`);
      this.connect();
    }, delay);
  }

  /**
   * Handle cache invalidation hints from WebSocket
   */
  private handleCacheHint(hint: DemoWebSocketHint): void {
    this.log('Received cache hint', hint);

    // Emit event to listeners
    this.onEvent?.(hint.event);

    // Apply cache invalidation based on event type
    const { event } = hint;

    switch (event.type) {
      case 'execution_updated':
        demoCacheInvalidation.invalidateExecution(this.queryClient, event.executionId);
        break;

      case 'stage_completed':
        if (event.stageName) {
          demoCacheInvalidation.invalidateStage(this.queryClient, event.executionId, event.stageName);
        }
        break;

      case 'billing_commit':
        // Invalidate billing-related queries using centralized keys
        this.queryClient.invalidateQueries({
          queryKey: demoQueryKeys.billingCommits(event.executionId)
        });
        // Invalidate all API call aggregates for this execution
        this.queryClient.invalidateQueries({
          queryKey: demoQueryKeys.billing()
        });
        break;

      case 'temporal_event':
        // Invalidate temporal events for execution using centralized keys
        this.queryClient.invalidateQueries({
          queryKey: demoQueryKeys.temporalEvents(event.executionId)
        });
        break;

      case 'heartbeat':
        // Invalidate heartbeats for execution using centralized keys
        this.queryClient.invalidateQueries({
          queryKey: demoQueryKeys.temporalHeartbeats(event.executionId)
        });
        break;

      default:
        this.log('Unknown event type', event.type);
    }
  }

  /**
   * Subscribe to events for a specific execution ID
   */
  public subscribeToExecution(executionId: string): void {
    this.send({
      type: 'subscribe_execution',
      executionId
    });
  }

  /**
   * Unsubscribe from events for a specific execution ID
   */
  public unsubscribeFromExecution(executionId: string): void {
    this.send({
      type: 'unsubscribe_execution',
      executionId
    });
  }
}

/**
 * Global WebSocket client instance - lazy initialized
 */
let globalDemoWsClient: DemoWebSocketClient | null = null;

/**
 * Get or create the global Demo WebSocket client
 */
export function getDemoWebSocketClient(queryClient: QueryClient): DemoWebSocketClient {
  if (!globalDemoWsClient) {
    globalDemoWsClient = new DemoWebSocketClient({
      queryClient,
      enableLogging: import.meta.env.DEV,
    });
  }

  return globalDemoWsClient;
}

/**
 * React hook for managing Demo WebSocket connection
 */
export function useDemoWebSocket(queryClient: QueryClient, options?: {
  executionId?: string;
  autoConnect?: boolean;
  onEvent?: (event: DemoWebSocketEvent) => void;
  onError?: (error: Error) => void;
  onConnectionChange?: (connected: boolean) => void;
}) {
  const wsClient = getDemoWebSocketClient(queryClient);

  // Set up event listeners
  if (options?.onEvent) {
    wsClient['onEvent'] = options.onEvent;
  }
  if (options?.onError) {
    wsClient['onError'] = options.onError;
  }
  if (options?.onConnectionChange) {
    wsClient['onConnectionChange'] = options.onConnectionChange;
  }

  // Auto-connect if enabled
  if (options?.autoConnect !== false) {
    wsClient.connect();
  }

  // Subscribe to execution if provided
  if (options?.executionId) {
    wsClient.subscribeToExecution(options.executionId);
  }

  return {
    client: wsClient,
    isConnected: wsClient.isConnectionOpen(),
    connect: () => wsClient.connect(),
    disconnect: () => wsClient.disconnect(),
    subscribeToExecution: (id: string) => wsClient.subscribeToExecution(id),
    unsubscribeFromExecution: (id: string) => wsClient.unsubscribeFromExecution(id),
  };
}